import { useState, useEffect, useRef } from 'react';
import { useCart } from '../../hooks/useCart';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import { getProductImage } from '../../utils/imageStorage';

export default function ProduitChandelierLivrePlieBlancPerles() {
  const { addItem } = useCart();
  const [quantity, setQuantity] = useState(1);
  const [notification, setNotification] = useState({ show: false, message: '', type: 'success' });
  const notificationTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isMountedRef = useRef(true);
  const [productImage, setProductImage] = useState<string>('');
  const [imageLoaded, setImageLoaded] = useState(false);

  const productId = 'chandelier-livre-plie-blanc-perles';
  const defaultImage = 'https://static.readdy.ai/image/fd456b0d540e8f517714341c204b3126/3ef1ee7de7e5e4757619c328190956c7.png';

  useEffect(() => {
    isMountedRef.current = true;
    
    const loadImage = async () => {
      try {
        const image = await getProductImage(productId);
        if (isMountedRef.current) {
          setProductImage(image || defaultImage);
          setImageLoaded(true);
        }
      } catch (error) {
        console.error('Erreur lors du chargement de l\'image:', error);
        if (isMountedRef.current) {
          setProductImage(defaultImage);
          setImageLoaded(true);
        }
      }
    };
    
    loadImage();
    
    return () => {
      isMountedRef.current = false;
      if (notificationTimeoutRef.current) {
        clearTimeout(notificationTimeoutRef.current);
        notificationTimeoutRef.current = null;
      }
    };
  }, []);

  const product = {
    id: productId,
    name: 'Chandelier en livre plié – Blanc & Perles (pièce unique)',
    price: 30,
    image: productImage,
    description: 'Chandelier décoratif en livre plié, sublimé par de la dentelle délicate, de petites perles nacrées et un ruban blanc',
    category: 'Collection de Noël',
    subcategory: 'Chandelier'
  };

  const showNotification = (message: string, type: 'success' | 'error' = 'success') => {
    if (!isMountedRef.current) return;
    
    if (notificationTimeoutRef.current) {
      clearTimeout(notificationTimeoutRef.current);
      notificationTimeoutRef.current = null;
    }

    setNotification({
      show: true,
      message,
      type
    });

    notificationTimeoutRef.current = setTimeout(() => {
      if (isMountedRef.current) {
        setNotification(prev => ({ ...prev, show: false }));
      }
      notificationTimeoutRef.current = null;
    }, 3000);
  };

  const handleAddToCart = () => {
    if (!isMountedRef.current) return;
    
    try {
      for (let i = 0; i < quantity; i++) {
        addItem({
          id: product.id,
          name: product.name,
          price: product.price,
          image: product.image,
          description: product.description
        });
      }

      showNotification(`${product.name} ajouté au panier !`, 'success');

    } catch (error) {
      console.error('Erreur lors de l\'ajout au panier:', error);
      showNotification('Erreur lors de l\'ajout au panier', 'error');
    }
  };

  return (
    <div className="min-h-screen bg-[#F8EDEA]">
      <Header />

      <main className="py-12 sm:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Fil d'Ariane */}
          <nav className="mb-8">
            <ol className="flex flex-wrap items-center space-x-2 text-sm" style={{ fontFamily: 'Poppins, sans-serif' }}>
              <li>
                <Link to="/" className="text-[#B97A72] hover:text-[#C49B86] transition-colors">
                  Accueil
                </Link>
              </li>
              <li className="text-[#6B5E5A]">/</li>
              <li>
                <Link to="/boutique" className="text-[#B97A72] hover:text-[#C49B86] transition-colors">
                  Boutique
                </Link>
              </li>
              <li className="text-[#6B5E5A]">/</li>
              <li className="text-[#6B5E5A]">Chandelier en livre plié – Blanc & Perles</li>
            </ol>
          </nav>

          {/* Contenu principal */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
            {/* Galerie d'images */}
            <div className="space-y-4">
              <div className="aspect-square bg-[#FAF9F8] border-2 border-[#B97A72] rounded-2xl overflow-hidden">
                {!imageLoaded ? (
                  <div className="w-full h-full flex items-center justify-center">
                    <i className="ri-loader-4-line text-4xl text-[#B97A72] animate-spin"></i>
                  </div>
                ) : (
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-cover"
                  />
                )}
              </div>
            </div>

            {/* Informations produit */}
            <div className="space-y-6">
              {/* Badge pièce unique */}
              <div className="inline-block">
                <span className="bg-[#C49B86] text-white px-4 py-2 rounded-full text-sm font-medium">
                  Pièce unique ✨
                </span>
              </div>

              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#B97A72]" style={{ fontFamily: 'Allura, cursive' }}>
                {product.name}
              </h1>

              <div className="flex items-baseline space-x-3">
                <span className="text-4xl font-bold text-[#B97A72]" style={{ fontFamily: 'Playfair Display, serif' }}>
                  {product.price}€
                </span>
              </div>

              {/* Description */}
              <div className="prose prose-sm sm:prose-base max-w-none">
                <div className="text-[#6B5E5A] space-y-4" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  <p className="text-lg font-semibold text-[#B97A72]">
                    ✨ Chandelier en livre plié – Blanc & Perles (pièce unique)
                  </p>
                  
                  <p>
                    Ce chandelier décoratif est une création artisanale réalisée à partir d'un livre entièrement plié à la main, formant une structure élégante à plusieurs niveaux. Il est sublimé par de la dentelle délicate, de petites perles nacrées et un ruban blanc pour une finition douce et raffinée.
                  </p>

                  <p>
                    La bougie placée au sommet apporte une lumière chaleureuse, parfaite pour créer une ambiance cocooning, chic et poétique pendant les fêtes de Noël.
                  </p>

                  <p>
                    Chaque création est unique, façonnée avec soin dans mon atelier à partir de livres recyclés. Aucun chandelier ne peut être reproduit à l'identique.
                  </p>

                  <div className="bg-[#FAF9F8] border-2 border-[#B97A72] rounded-lg p-4 my-4">
                    <p className="font-semibold text-[#B97A72] mb-2">🕯️ Idéal pour :</p>
                    <ul className="space-y-1 list-disc list-inside">
                      <li>Une décoration de table élégante et lumineuse</li>
                      <li>Un cadeau artisanal raffiné</li>
                      <li>Apporter une touche douce et poétique à un intérieur</li>
                    </ul>
                  </div>

                  <div className="space-y-2">
                    <p className="flex items-center">
                      <i className="ri-check-line text-[#B97A72] mr-2"></i>
                      <strong>Création 100 % faite main</strong>
                    </p>
                    <p className="flex items-center">
                      <i className="ri-check-line text-[#B97A72] mr-2"></i>
                      <strong>Pièce unique</strong>
                    </p>
                    <p className="flex items-center">
                      <i className="ri-check-line text-[#B97A72] mr-2"></i>
                      <strong>Livre recyclé – démarche éco-responsable</strong>
                    </p>
                    <p className="flex items-center">
                      <i className="ri-check-line text-[#B97A72] mr-2"></i>
                      <strong>Décorations dentelle, perles et ruban incluses</strong>
                    </p>
                  </div>
                </div>
              </div>

              {/* Sélecteur de quantité */}
              <div className="space-y-4">
                <div className="flex items-center space-x-4">
                  <label className="text-[#6B5E5A] font-medium" style={{ fontFamily: 'Playfair Display, serif' }}>
                    Quantité :
                  </label>
                  <div className="flex items-center border-2 border-[#B97A72] rounded-lg overflow-hidden">
                    <button
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      className="px-4 py-2 bg-[#FAF9F8] hover:bg-[#EBD2CF] transition-colors cursor-pointer"
                    >
                      <i className="ri-subtract-line text-[#B97A72]"></i>
                    </button>
                    <span className="px-6 py-2 bg-white text-[#6B5E5A] font-medium" style={{ fontFamily: 'Poppins, sans-serif' }}>
                      {quantity}
                    </span>
                    <button
                      onClick={() => setQuantity(quantity + 1)}
                      className="px-4 py-2 bg-[#FAF9F8] hover:bg-[#EBD2CF] transition-colors cursor-pointer"
                    >
                      <i className="ri-add-line text-[#B97A72]"></i>
                    </button>
                  </div>
                </div>

                {/* Boutons d'action */}
                <div className="flex flex-col sm:flex-row gap-4">
                  <button
                    onClick={handleAddToCart}
                    className="flex-1 bg-[#B97A72] text-white px-8 py-4 rounded-full hover:bg-[#C49B86] transition-colors whitespace-nowrap cursor-pointer text-lg font-medium"
                    style={{ fontFamily: 'Playfair Display, serif' }}
                  >
                    <i className="ri-shopping-cart-line mr-2"></i>
                    Ajouter au panier
                  </button>
                  <Link
                    to="/boutique"
                    className="flex-1 bg-transparent text-[#B97A72] px-8 py-4 rounded-full border-2 border-[#B97A72] hover:bg-[#B97A72] hover:text-white transition-colors whitespace-nowrap cursor-pointer text-lg font-medium text-center"
                    style={{ fontFamily: 'Playfair Display, serif' }}
                  >
                    Continuer mes achats
                  </Link>
                </div>
              </div>

              {/* Informations de livraison */}
              <div className="bg-[#FAF9F8] border-2 border-[#B97A72] rounded-lg p-6 space-y-3">
                <div className="flex items-start space-x-3">
                  <i className="ri-truck-line text-xl text-[#B97A72] mt-1"></i>
                  <div>
                    <p className="font-semibold text-[#6B5E5A]" style={{ fontFamily: 'Playfair Display, serif' }}>
                      Livraison soignée
                    </p>
                    <p className="text-sm text-[#6B5E5A]" style={{ fontFamily: 'Poppins, sans-serif' }}>
                      Emballage protecteur pour garantir l'arrivée en parfait état
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <i className="ri-hand-heart-line text-xl text-[#B97A72] mt-1"></i>
                  <div>
                    <p className="font-semibold text-[#6B5E5A]" style={{ fontFamily: 'Playfair Display, serif' }}>
                      Fait main avec amour
                    </p>
                    <p className="text-sm text-[#6B5E5A]" style={{ fontFamily: 'Poppins, sans-serif' }}>
                      Chaque création est unique et réalisée dans mon atelier en Provence
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Produits similaires */}
          <section className="mt-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#C49B86] mb-8 text-center" style={{ fontFamily: 'Allura, cursive' }}>
              Vous aimerez aussi
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Produit 1 */}
              <Link to="/produit-chandelier-livre-plie-dentelle-perles" className="group">
                <article className="bg-[#FAF9F8] border-2 border-[#B97A72] rounded-lg overflow-hidden hover:shadow-xl transition-all">
                  <div className="aspect-square bg-[#F3D9D9] relative overflow-hidden">
                    <img
                      src="https://static.readdy.ai/image/fd456b0d540e8f517714341c204b3126/d529905dc80190c446bfa46703479507.png"
                      alt="Chandelier en livre plié – Dentelle & Perles"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-semibold text-[#6B5E5A] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                      Chandelier en livre plié – Dentelle & Perles
                    </h3>
                    <p className="text-2xl font-bold text-[#B97A72]" style={{ fontFamily: 'Playfair Display, serif' }}>
                      30€
                    </p>
                  </div>
                </article>
              </Link>

              {/* Produit 2 */}
              <Link to="/produit-bougie-livre-plie-avec-led-v2" className="group">
                <article className="bg-[#FAF9F8] border-2 border-[#B97A72] rounded-lg overflow-hidden hover:shadow-xl transition-all">
                  <div className="aspect-square bg-[#F3D9D9] relative overflow-hidden">
                    <img
                      src="https://static.readdy.ai/image/fd456b0d540e8f517714341c204b3126/842983f7d1219d4e05abdf64e978f33f.png"
                      alt="Bougie en livre plié – Avec LED"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-semibold text-[#6B5E5A] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                      Bougie en livre plié – Avec LED
                    </h3>
                    <p className="text-2xl font-bold text-[#B97A72]" style={{ fontFamily: 'Playfair Display, serif' }}>
                      30€
                    </p>
                  </div>
                </article>
              </Link>

              {/* Produit 3 */}
              <Link to="/produit-sapin-lumineux-dore" className="group">
                <article className="bg-[#FAF9F8] border-2 border-[#B97A72] rounded-lg overflow-hidden hover:shadow-xl transition-all">
                  <div className="aspect-square bg-[#F3D9D9] relative overflow-hidden">
                    <img
                      src="https://static.readdy.ai/image/fd456b0d540e8f517714341c204b3126/3eac711adf0e5d03db76dab7cee730d9.png"
                      alt="Sapin lumineux doré"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-semibold text-[#6B5E5A] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                      Sapin lumineux doré – Livre plié
                    </h3>
                    <p className="text-2xl font-bold text-[#B97A72]" style={{ fontFamily: 'Playfair Display, serif' }}>
                      30€
                    </p>
                  </div>
                </article>
              </Link>
            </div>
          </section>
        </div>
      </main>

      {/* Notification toast */}
      {notification.show && (
        <div 
          className={`fixed top-20 right-4 z-50 px-6 py-3 rounded-lg shadow-lg transition-all duration-300 ${
            notification.type === 'success' 
              ? 'bg-green-500 text-white' 
              : 'bg-red-500 text-white'
          }`}
          style={{ 
            transform: notification.show ? 'translateX(0)' : 'translateX(100%)',
            opacity: notification.show ? 1 : 0
          }}
        >
          <div className="flex items-center space-x-2">
            <i className={`${notification.type === 'success' ? 'ri-check-line' : 'ri-error-warning-line'}`}></i>
            <span style={{ fontFamily: 'Poppins, sans-serif' }}>{notification.message}</span>
          </div>
        </div>
      )}
    </div>
  );
}
